import z from 'zod';

/**
 * Where the ticket came from.
 *
 * The AI sources are split by channel rather than lumped into one
 * `ai` value: a WhatsApp escalation and an in-app one need different
 * follow-up (a reply on WhatsApp reaches the person on their phone; an
 * in-app one may sit unread for a day), and the split is what makes
 * "how many issues does WhatsApp generate" answerable without joining
 * back to the conversation.
 */
export const ticketSourceSchema = z.enum([
  'ai_whatsapp',
  'ai_app',
  'admin_manual',
  'dashboard',
  'phone',
  'email',
  'other',
]);
export type TicketSource = z.infer<typeof ticketSourceSchema>;

/**
 * Lifecycle of a support ticket.
 *
 * `waiting_on_customer` is a real state, not a flavour of `open`: it is
 * the difference between "Hero owes an answer" and "the customer does",
 * and every support queue that lacks it ends up chasing its own tail.
 */
export const ticketStatusSchema = z.enum([
  'open',
  'in_progress',
  'waiting_on_customer',
  'resolved',
  'closed',
]);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

/** Statuses that no longer need anyone to act. */
export const TICKET_TERMINAL_STATUSES: TicketStatus[] = ['resolved', 'closed'];

export const ticketPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
export type TicketPriority = z.infer<typeof ticketPrioritySchema>;

/**
 * What the ticket is about. Coarse on purpose — the categories exist to
 * route a ticket to the right person and to answer "what do people
 * struggle with", not to describe the problem. The description does that.
 */
export const ticketCategorySchema = z.enum([
  'order',
  'waybill',
  'payment',
  'account',
  'integration',
  'app_issue',
  'feature_request',
  'complaint',
  'other',
]);
export type TicketCategory = z.infer<typeof ticketCategorySchema>;
